import { DELIVERY_STAGE_FILTER_OPTIONS } from '@/components/deal/vehicle/delivery-stage/delivery-stage-options';
import { Checkbox } from '@/components/ui/checkbox';
import { ComboboxPopover } from '@/components/ui/combobox/combobox-popover';
import { cn, pluralize } from '@/lib/utils';
import type { DeliveryStage } from '@/models/types';
import { useMemo, type ReactNode } from 'react';
import { useFilteredDealsContext } from '../filtered-deals/filtered-deals-provider';
import type { DeliveryStageFilters } from '../types';

const DeliveryStageFiltersComboBoxPopover = ({
  children,
  comboBoxOnChange,
  comboBoxOpen,
  onOpenChange,
  filters: deliveryStageFilters,
  stayOpenAfterSelection = true,
  alignOffset,
  side = 'bottom',
  align,
  popoverContentClassName,
}: {
  children: ReactNode;
  comboBoxOnChange: (value: string) => void;
  comboBoxOpen: boolean;
  onOpenChange: (open: boolean) => void;
  filters: DeliveryStageFilters;
  stayOpenAfterSelection: boolean;
  alignOffset?: number;
  side?: 'left' | 'top' | 'right' | 'bottom' | undefined;
  align?: 'center' | 'start' | 'end' | undefined;
  popoverContentClassName?: string;
}) => {
  const { getCount } = useFilteredDealsContext();

  const comboBoxOptions = useMemo(() => {
    return DELIVERY_STAGE_FILTER_OPTIONS.map((option) => {
      const count = getCount('delivery_stage', option.value as DeliveryStage);

      return {
        label: (
          <div className="flex flex-row items-center gap-2 text-nowrap">
            {option.icon}
            {option.label}
            <span className="text-xs text-gray-400">
              {count} {pluralize(count, 'deal', 'deals')}
            </span>
          </div>
        ),
        value: option.value,
        icon: (
          <div className="size-4">
            <Checkbox
              checked={deliveryStageFilters[option.value as DeliveryStage]}
            />
          </div>
        ),
      };
    });
  }, [deliveryStageFilters, getCount]);

  return (
    <ComboboxPopover
      placeholder={'Delivery Stage...'}
      options={comboBoxOptions}
      onChange={comboBoxOnChange}
      align={align}
      side={side}
      alignOffset={alignOffset}
      endAdornment={undefined}
      emptyMessage={undefined}
      open={comboBoxOpen}
      onOpenChange={onOpenChange}
      stayOpenAfterSelection={stayOpenAfterSelection}
      popoverContentClassName={cn(
        'w-fit min-w-[200px]',
        popoverContentClassName,
      )}
    >
      {children}
    </ComboboxPopover>
  );
};

export default DeliveryStageFiltersComboBoxPopover;
